import {
  FeaturePermissions,
  useAppSelector,
  UserVoice,
  useTranslatedVoiceSampleScript,
  useTranslation,
  useUserInfo,
  useUserVoices,
  VoiceStatus,
} from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import { Gender } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useVoicePreview from "@/pages/VoiceClonePreview/useVoicePreview";

import { getVoiceById, priortizeVoiceId, transformVoices } from "../../utils";
import { PREFERRED_VOICE_ACTIONS } from "../voices/constant";
import { IChatMessageAudioContainerProps, IChatVoice, PREFFERRED_VOICE_API_ACTIONS, VoiceCategories } from "../voices/types";
import { ChatModalVoiceSelection } from "../VoiceSelectionModal";

let audio = new Audio();
const stopAudioPlay = () => {
  audio.src = "";
};

const ChatMessageAudioContainer = ({
  voiceId,
  conversationId,
  showVoiceSelectionModal,
  setShowVoiceSelectionModal,
  characterId,
  defaultPersonaId,
  handlePreferredVoiceActions,
  characterDefaultVoiceId,
}: IChatMessageAudioContainerProps) => {
  const { t } = useTranslation();
  const { getTranslatedVoiceSampleScript } = useTranslatedVoiceSampleScript();
  const [selectedCategory, setSelectedCategory] = useState<VoiceCategories>(VoiceCategories.ALL);
  const [defaultVoiceId, setDefaultVoiceId] = useState(voiceId);
  const [stateIsVoiceSelectionLoading, setStateIsVoiceSelectionLoading] = useState(false);
  const allVoices = useAppSelector((state) => state?.voices?.allVoices);
  const { permissions } = useUserInfo();
  const { userVoices, getUserVoices, isLoading: isUserVoicesLoading } = useUserVoices();
  const hasCustomVoicesAccess = permissions?.includes(FeaturePermissions.TTS_CUSTOM_VOICES) ?? false;
  const { hasFeatureFlag: hasCustomVoiceFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const canUseCustomVoices = hasCustomVoiceFeatureFlag && hasCustomVoicesAccess;
  const refCustomPreviewReset = useRef<(() => void) | null>(null);
  const {
    previewVoice,
    stopPreview: stopCustomVoicePreview,
    isGenerating: isCustomVoiceGenerating,
    isPlaying: isCustomVoicePlaying,
  } = useVoicePreview();

  useEffect(() => {
    setDefaultVoiceId(voiceId);
  }, [voiceId]);

  const closeVoiceSelectionModal = () => {
    setShowVoiceSelectionModal?.(false);
  };

  const handleSuccess = (apiAction: PREFFERRED_VOICE_API_ACTIONS) => {
    switch (apiAction) {
      case PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID: {
        closeVoiceSelectionModal();
        setStateIsVoiceSelectionLoading(false);
        break;
      }
    }
  };

  const handleError = (apiAction: PREFFERRED_VOICE_API_ACTIONS) => {
    switch (apiAction) {
      case PREFFERRED_VOICE_API_ACTIONS.GET_ALL_VOICES: {
        closeVoiceSelectionModal();
        break;
      }
      case PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID: {
        setDefaultVoiceId(voiceId);
        setStateIsVoiceSelectionLoading(false);
        break;
      }
    }
  };

  useEffect(() => {
    if (showVoiceSelectionModal) {
      handlePreferredVoiceActions({
        action: PREFERRED_VOICE_ACTIONS.Get_Voices,
        errorCB: () => handleError(PREFFERRED_VOICE_API_ACTIONS.GET_ALL_VOICES),
      });
      if (canUseCustomVoices) {
        void getUserVoices();
      }
    }
  }, [canUseCustomVoices, getUserVoices, handlePreferredVoiceActions, showVoiceSelectionModal]);

  const stopAllPreviewAudio = useCallback(() => {
    stopAudioPlay();
    refCustomPreviewReset.current?.();
    refCustomPreviewReset.current = null;
    stopCustomVoicePreview();
  }, [stopCustomVoicePreview]);

  useEffect(() => {
    return () => {
      stopAllPreviewAudio();
    };
  }, [stopAllPreviewAudio]);

  useEffect(() => {
    if (isCustomVoiceGenerating || isCustomVoicePlaying || !refCustomPreviewReset.current) {
      return;
    }

    refCustomPreviewReset.current();
    refCustomPreviewReset.current = null;
  }, [isCustomVoiceGenerating, isCustomVoicePlaying]);

  const playVoiceSample = useCallback(
    (shouldPlay: boolean, onAudioEnded: () => void, audioUrl?: string) => {
      if (!shouldPlay) {
        stopAudioPlay();
        return;
      }
      if (!audioUrl) {
        return;
      }
      if (!audio) {
        audio = new Audio();
      }
      handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
      audio.src = audioUrl;
      void audio.play();
      audio.removeEventListener("ended", onAudioEnded);
      audio.addEventListener("ended", onAudioEnded);
    },
    [handlePreferredVoiceActions],
  );

  const transformedPlatformVoices = useMemo<IChatVoice[]>(() => transformVoices(allVoices, playVoiceSample), [allVoices, playVoiceSample]);

  const filterVoicesByGender = (gender: Gender) => {
    return transformedPlatformVoices.filter((voice) => voice.gender === gender);
  };

  const transformedUserVoices = useMemo<IChatVoice[]>(
    () =>
      userVoices
        .filter((voice: UserVoice) => voice.status === VoiceStatus.READY_TO_USE)
        .map((voice: UserVoice) => ({
          id: voice.id,
          name: voice.name,
          label: voice.name,
          subLabel: voice.description,
          description: voice.description,
          audio_url: "",
          isCustom: true,
          onChange: (shouldPlay: boolean, reset: () => void) => {
            if (!shouldPlay) {
              refCustomPreviewReset.current?.();
              refCustomPreviewReset.current = null;
              stopCustomVoicePreview();
              return;
            }
            const sampleText = getTranslatedVoiceSampleScript(voice.language);
            refCustomPreviewReset.current = reset;
            void previewVoice(sampleText || voice.description || voice.name || "", voice.id);
          },
        })),
    [previewVoice, stopCustomVoicePreview, userVoices, getTranslatedVoiceSampleScript],
  );

  const allSelectableVoices = useMemo<IChatVoice[]>(
    () => (canUseCustomVoices ? [...transformedPlatformVoices, ...transformedUserVoices] : transformedPlatformVoices),
    [canUseCustomVoices, transformedPlatformVoices, transformedUserVoices],
  );

  const voices = useMemo((): IChatVoice[] => {
    let updatedVoices: IChatVoice[];
    if (selectedCategory === VoiceCategories.MALE) {
      updatedVoices = filterVoicesByGender(Gender.Male);
    } else if (selectedCategory === VoiceCategories.FEMALE) {
      updatedVoices = filterVoicesByGender(Gender.Female);
    } else if (selectedCategory === VoiceCategories.MY_VOICES) {
      updatedVoices = canUseCustomVoices ? transformedUserVoices : [];
    } else {
      updatedVoices = allSelectableVoices;
    }

    return priortizeVoiceId(updatedVoices, voiceId);
  }, [allSelectableVoices, canUseCustomVoices, conversationId, selectedCategory, transformedUserVoices, voiceId]);

  const onSelectVoiceId = useCallback(
    async (selectedVoiceId: IChatVoice["id"]) => {
      if (selectedVoiceId === undefined || selectedVoiceId === null) {
        return;
      }
      setDefaultVoiceId(selectedVoiceId);
      stopAllPreviewAudio();
      setStateIsVoiceSelectionLoading(true);
      const selectedVoiceName = getVoiceById(allSelectableVoices, selectedVoiceId)?.name ?? "";
      handlePreferredVoiceActions({
        action: PREFERRED_VOICE_ACTIONS.Set_Conversation_Voice_Id,
        selectedVoiceId,
        selectedVoiceName,
        characterId,
        defaultPersonaId,
        errorCB: () => handleError(PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID),
        successCB: () => handleSuccess(PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID),
      });
    },
    [allSelectableVoices, characterId, defaultPersonaId, handlePreferredVoiceActions, stopAllPreviewAudio],
  );

  const onPressCategory = useCallback(
    (category: VoiceCategories) => {
      setSelectedCategory(category);
      stopAllPreviewAudio();
    },
    [stopAllPreviewAudio],
  );

  const voiceCategoryList = useMemo(
    () => [
      { name: VoiceCategories.ALL, label: t("common:all") },
      { name: VoiceCategories.MALE, label: t("common:male") },
      { name: VoiceCategories.FEMALE, label: t("common:female") },
      ...(canUseCustomVoices ? [{ name: VoiceCategories.MY_VOICES, label: VoiceCategories.MY_VOICES }] : []),
    ],
    [canUseCustomVoices, t],
  );

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (stateIsVoiceSelectionLoading) {
        return;
      }
      stopAllPreviewAudio();
      if (!isOpen) {
        closeVoiceSelectionModal();
      }
    },
    [stopAllPreviewAudio, stateIsVoiceSelectionLoading],
  );

  return (
    <CapabilityGate capability={SubCapabilityKey.TtsCharacterAttach}>
      <ChatModalVoiceSelection
        dataTestId="TtsCharacterAttachCapabilityGate"
        open={showVoiceSelectionModal}
        listItems={voices}
        loading={!allVoices?.length}
        loadingList={stateIsVoiceSelectionLoading || isUserVoicesLoading}
        onVoiceSelection={onSelectVoiceId}
        onSearch={stopAllPreviewAudio}
        stopAudio={stopAllPreviewAudio}
        defaultSelectedVoice={defaultVoiceId}
        voiceSelectionLoading={stateIsVoiceSelectionLoading}
        onOpenChange={onOpenChange}
        voiceCategoryList={voiceCategoryList}
        selectedCategory={selectedCategory}
        onPressCategory={onPressCategory}
        characterDefaultVoiceId={characterDefaultVoiceId}
      />
    </CapabilityGate>
  );
};

export default ChatMessageAudioContainer;
